import { Dispatch, SetStateAction, useState, VFC } from 'react'
import { CheckboxDiv } from 'src/elements/Checkbox'
import {
  MasterData,
  MasterDataWithCategory,
  MasterDataWithRegionalDivisionType,
} from 'src/models/MasterData'
import { bordergray, orange } from 'src/styles/colors'
import { fontWeightBold } from 'src/styles/font'
import styled, { css } from 'styled-components'
import {
  Control,
  DeselectButton,
  SelectableItemDiv,
  SelectableItems,
} from '../formComponents/common'

export type Types =
  | 'businessTypes'
  | 'numberOfEmployees'
  | 'needs'
  | 'solutions'
  | 'prefectures'
  | 'positions'
  | 'capitals'
  | 'others'
type NestedTypes =
  | 'businessObjects'
  | 'foundedAts'
  | 'adminAges'
  | 'roleForAnalysis'

type PersonaInputFormProps = {
  items: MasterData[]
  checkedArray: MasterData[]
  selectedType: Types | NestedTypes
  setCheckedArray: Dispatch<SetStateAction<MasterData[]>>
  isNested?: boolean
  nestedName?: string
}

export const PersonaInputForm: VFC<PersonaInputFormProps> = ({
  items,
  checkedArray,
  selectedType,
  setCheckedArray,
  isNested,
  nestedName,
}) => {
  // NOTE: 該当する都道府県全てがcheckedの場合、地域のcheckboxをcheckedにする
  const handleChecked = items?.every(({ value }) => {
    const checked = checkedArray.find((checked) => checked.value === value)
    if (!checked) return false
    return checked.value === value
  })
  const updateSelectedValue = (selected: MasterData, checked: boolean) => {
    checked
      ? setCheckedArray([...checkedArray, selected])
      : setCheckedArray(
          checkedArray.filter((item) => item.value !== selected.value),
        )
  }
  const selectAll = () => {
    setCheckedArray(items)
  }
  const deselectAll = () => {
    setCheckedArray([])
  }
  return (
    <>
      {isNested && (
        <>
          <NestedChecklabelHeader>
            <CheckboxDiv color={orange}>
              <input
                id={selectedType}
                type="checkbox"
                onChange={({ target: { checked } }) =>
                  checked ? selectAll() : deselectAll()
                }
                checked={handleChecked}
              />
              <label htmlFor={selectedType}>{nestedName}</label>
            </CheckboxDiv>
          </NestedChecklabelHeader>
        </>
      )}
      {!isNested && (
        <Control>
          <button type="button" onClick={() => selectAll()}>
            すべて選択
          </button>
          <DeselectButton type="button" onClick={() => deselectAll()}>
            解除
          </DeselectButton>
        </Control>
      )}
      <SelectableItems>
        {items.map((option) => (
          <SelectableItemDiv key={`${selectedType}_${option.value}`}>
            <input
              id={`${selectedType}_${option.value}`}
              type="checkbox"
              onChange={({ target: { checked } }) =>
                updateSelectedValue(option, checked)
              }
              checked={checkedArray.some(({ value }) => option.value === value)}
            />
            <label htmlFor={`${selectedType}_${option.value}`}>
              {option.label}
            </label>
          </SelectableItemDiv>
        ))}
      </SelectableItems>
    </>
  )
}

type PrefectureInputFormProps = {
  prefectures: MasterDataWithRegionalDivisionType[]
  checkedArray: MasterDataWithRegionalDivisionType[]
  name: string
  id: string
  setCheckedArray: Dispatch<
    SetStateAction<MasterDataWithRegionalDivisionType[]>
  >
}

export const PrefectureInputForm: VFC<PrefectureInputFormProps> = ({
  prefectures,
  checkedArray,
  name,
  id,
  setCheckedArray,
}) => {
  const [opened, setOpened] = useState(false)
  const updateSelectedPrefecture = (checked: boolean) => {
    if (checked) {
      // addCheckedArray checkedArray に propsで渡された prefectures を追加した配列
      const addCheckedArray = [
        ...checkedArray,
        // NOTE: checkedArrayに含まれているprefecturesは除外する(重複対策)
        ...prefectures.filter((item) => !checkedArray.includes(item)),
      ]
      setCheckedArray(addCheckedArray)
    } else {
      // excludedCheckedArray checkedArray から propsで渡された prefectures を除外した配列
      const excludedCheckedArray = checkedArray.filter((item) => {
        const prefecture = prefectures.find(
          (prefecture) => prefecture.value === item.value,
        )
        // NOTE: findで見つからない場合はpropsで渡されていないprefecturesなのでfilterで除外しない
        if (!prefecture) return true
        return item.value !== prefecture.value
      })
      setCheckedArray(excludedCheckedArray)
    }
  }
  // NOTE: 該当する都道府県全てがcheckedの場合、地域のcheckboxをcheckedにする
  const handleChecked = prefectures?.every(({ value }) => {
    const checked = checkedArray.find((checked) => checked.value === value)
    if (!checked) return false
    return checked.value === value
  })
  const updateSelectedValue = (
    selected: MasterDataWithRegionalDivisionType,
    checked: boolean,
  ) => {
    checked
      ? setCheckedArray([...checkedArray, selected])
      : setCheckedArray(
          checkedArray.filter((item) => item.value !== selected.value),
        )
  }
  return (
    <>
      <ChecklabelHeader onClick={() => setOpened(!opened)} opened={opened}>
        <HeaderLabel opened={opened}>
          <CheckboxDiv color={orange}>
            <input
              id={id}
              type="checkbox"
              onChange={({ target: { checked } }) =>
                updateSelectedPrefecture(checked)
              }
              checked={handleChecked}
            />
            <label htmlFor={id}>{name}</label>
          </CheckboxDiv>
        </HeaderLabel>
      </ChecklabelHeader>
      <Accordion opened={opened}>
        <SelectablePrefectures>
          {prefectures?.map((option) => (
            <SelectableItemDiv key={`${id}_${option.value}`}>
              <input
                id={`${id}_${option.value}`}
                type="checkbox"
                onChange={({ target: { checked } }) =>
                  updateSelectedValue(option, checked)
                }
                checked={checkedArray.some(
                  ({ value }) => option.value === value,
                )}
              />
              <label htmlFor={`${id}_${option.value}`}>{option.label}</label>
            </SelectableItemDiv>
          ))}
        </SelectablePrefectures>
      </Accordion>
    </>
  )
}

type PersonaInputDateFormProps = {
  title?: string
  start: string
  end: string
  setStart: Dispatch<SetStateAction<string>>
  setEnd: Dispatch<SetStateAction<string>>
}

export const PersonaInputDateForm: VFC<PersonaInputDateFormProps> = ({
  title,
  start,
  end,
  setStart,
  setEnd,
}) => {
  return (
    <>
      <Header>
        <label>{title}</label>
      </Header>
      <SelectableItems>
        <DateInput
          type="date"
          value={start}
          onChange={({ target: { value } }) => setStart(value)}
        />
        -
        <DateInput
          type="date"
          value={end}
          onChange={({ target: { value } }) => setEnd(value)}
        />
      </SelectableItems>
    </>
  )
}

const transitionTime = '0.2s'

const SelectablePrefectures = styled.div`
  display: flex;
  flex-wrap: wrap;
`

const ChecklabelHeader = styled.div<{ opened: boolean }>`
  position: relative;
  padding: 10px;
  font-size: 16px;
  font-weight: ${fontWeightBold};
  cursor: pointer;
  margin-top: 12px;

  ${({ opened }) =>
    // NOTE: transition-delayはaccordionをcloseし終わったらborder-bottomを表示するため
    !opened &&
    css`
      border-bottom: 1px solid ${bordergray};
      transition-delay: ${transitionTime};
      padding-bottom: 20px;
    `}
`

const HeaderLabel = styled.div<{ opened: boolean }>`
  cursor: pointer;
  input {
    transform: translate(0px, -2px);
  }
  label {
    margin-left: 3px;
  }
  label::after {
    content: '';
    display: inline-block;
    position: absolute;
    top: 17px;
    right: 15px;
    width: 8px;
    height: 8px;
    border-top: solid 2px #a8a9ac;
    border-right: solid 2px #a8a9ac;
    transform: rotate(45deg);
    transition: ${transitionTime};
    pointer-events: none;
  }
  ${({ opened }) =>
    opened &&
    css`
      label::after {
        transform: rotate(135deg);
      }
    `}
`

const Accordion = styled.div<{ opened: boolean }>`
  transition: ${transitionTime};
  ${({ opened }) =>
    opened
      ? css`
          border-bottom: 1px solid ${bordergray};
          padding-bottom: 10px;
          max-height: 500px;
          visibility: visible;
        `
      : css`
          max-height: 0px;
          visibility: hidden;
        `}
`

export const NestedChecklabelHeader = styled.div`
  padding: 0px 10px;
  font-size: 16px;
  font-weight: ${fontWeightBold};
  cursor: pointer;
  label {
    margin-left: 5px;
  }
  input {
    transform: translate(0px, -2px);
  }
`

export const Header = styled.div`
  padding: 0px 0px;
  font-size: 16px;
  font-weight: ${fontWeightBold};
  cursor: pointer;
  label {
    margin-left: 5px;
  }
`

export const DateInput = styled.input`
  font-size: 14px;
  border: 1px solid rgb(133, 133, 133);
  margin: 0px 8px;
`

type PersonaNeedsInputFormProps = {
  items: MasterDataWithCategory[]
  checkedArray: MasterData[]
  selectedType: Types
  setCheckedArray: Dispatch<SetStateAction<MasterData[]>>
}

export const PersonaNeedsInputForm: VFC<PersonaNeedsInputFormProps> = ({
  items,
  checkedArray,
  selectedType,
  setCheckedArray,
}) => {
  const categories = [
    {
      title: '経営戦略・事業開発',
      items: items.filter((item) => item.category === '経営戦略・事業開発'),
    },
    {
      title: 'ブランディング・マーケティング',
      items: items.filter(
        (item) => item.category === 'ブランディング・マーケティング',
      ),
    },
    {
      title: 'セールス強化・売上拡大',
      items: items.filter((item) => item.category === 'セールス強化・売上拡大'),
    },
    {
      title: '人材採用',
      items: items.filter((item) => item.category === '人材採用'),
    },
    {
      title: '人・組織づくり',
      items: items.filter((item) => item.category === '人・組織づくり'),
    },
    {
      title: '業務改善・システム開発・デジタル化',
      items: items.filter(
        (item) => item.category === '業務改善・システム開発・デジタル化',
      ),
    },
    {
      title: '財務・資金管理・バックオフィス',
      items: items.filter(
        (item) => item.category === '財務・資金管理・バックオフィス',
      ),
    },
    {
      title: 'その他',
      items: items.filter((item) => item.category === 'その他'),
    },
  ]

  const selectAll = () => {
    setCheckedArray(items)
  }

  const deselectAll = () => {
    setCheckedArray([])
  }

  return (
    <>
      <Control>
        <button type="button" onClick={() => selectAll()}>
          すべて選択
        </button>
        <DeselectButton type="button" onClick={() => deselectAll()}>
          解除
        </DeselectButton>
      </Control>
      {categories.map((category) => (
        <NeedsCategoryInputForm
          key={category.title}
          category={category.title}
          items={category.items}
          checkedArray={checkedArray}
          selectedType={selectedType}
          setCheckedArray={setCheckedArray}
        />
      ))}
    </>
  )
}

type PersonaSolutionsInputFormProps = {
  items: MasterDataWithCategory[]
  checkedArray: MasterData[]
  selectedType: Types
  setCheckedArray: Dispatch<SetStateAction<MasterData[]>>
}

export const PersonaSolutionsInputForm: VFC<PersonaSolutionsInputFormProps> = ({
  items,
  checkedArray,
  selectedType,
  setCheckedArray,
}) => {
  const categories = [
    {
      title: '経営戦略・事業開発',
      items: items.filter((item) => item.category === '経営戦略・事業開発'),
    },
    {
      title: 'ブランディング・マーケティング',
      items: items.filter(
        (item) => item.category === 'ブランディング・マーケティング',
      ),
    },
    {
      title: 'セールス強化・売上拡大',
      items: items.filter((item) => item.category === 'セールス強化・売上拡大'),
    },
    {
      title: '人材採用',
      items: items.filter((item) => item.category === '人材採用'),
    },
    {
      title: '人・組織づくり',
      items: items.filter((item) => item.category === '人・組織づくり'),
    },
    {
      title: '業務改善・システム開発・デジタル化',
      items: items.filter(
        (item) => item.category === '業務改善・システム開発・デジタル化',
      ),
    },
    {
      title: '財務・資金管理・バックオフィス',
      items: items.filter(
        (item) => item.category === '財務・資金管理・バックオフィス',
      ),
    },
    {
      title: 'その他',
      items: items.filter((item) => item.category === 'その他'),
    },
  ]

  const selectAll = () => {
    setCheckedArray(items)
  }

  const deselectAll = () => {
    setCheckedArray([])
  }

  return (
    <>
      <Control>
        <button type="button" onClick={() => selectAll()}>
          すべて選択
        </button>
        <DeselectButton type="button" onClick={() => deselectAll()}>
          解除
        </DeselectButton>
      </Control>
      {categories.map((category) => (
        <SolutionsCategoryInputForm
          key={category.title}
          category={category.title}
          items={category.items}
          checkedArray={checkedArray}
          selectedType={selectedType}
          setCheckedArray={setCheckedArray}
        />
      ))}
    </>
  )
}

type NeedsCategoryInputFormProps = {
  category: string
  items: MasterDataWithCategory[]
  checkedArray: MasterData[]
  selectedType: Types
  setCheckedArray: Dispatch<SetStateAction<MasterData[]>>
}

const NeedsCategoryInputForm: VFC<NeedsCategoryInputFormProps> = ({
  category,
  items,
  checkedArray,
  selectedType,
  setCheckedArray,
}) => {
  const [opened, setOpened] = useState(false)

  const updateSelectedCategory = (checked: boolean) => {
    if (checked) {
      // カテゴリ内のアイテムを追加（重複を除外）
      const addCheckedArray = [
        ...checkedArray,
        ...items.filter(
          (item) => !checkedArray.some((c) => c.value === item.value),
        ),
      ]
      setCheckedArray(addCheckedArray)
    } else {
      // カテゴリ内のアイテムを除外
      const excludedCheckedArray = checkedArray.filter((item) => {
        const categoryItem = items.find((i) => i.value === item.value)
        if (!categoryItem) return true
        return item.value !== categoryItem.value
      })
      setCheckedArray(excludedCheckedArray)
    }
  }

  // カテゴリ内のすべてのアイテムがチェック済みかどうか
  const allChecked = items?.every(({ value }) => {
    const checked = checkedArray.find((checked) => checked.value === value)
    if (!checked) return false
    return checked.value === value
  })

  // カテゴリ内の一部のアイテムがチェック済みかどうか
  const someChecked = items?.some(({ value }) => {
    return checkedArray.some((checked) => checked.value === value)
  })

  const isIndeterminate = someChecked && !allChecked

  const updateSelectedValue = (selected: MasterData, checked: boolean) => {
    checked
      ? setCheckedArray([...checkedArray, selected])
      : setCheckedArray(
          checkedArray.filter((item) => item.value !== selected.value),
        )
  }

  return (
    <>
      <ChecklabelHeader onClick={() => setOpened(!opened)} opened={opened}>
        <HeaderLabel opened={opened}>
          <CheckboxDiv color={orange}>
            <input
              id={`${selectedType}_${category}`}
              type="checkbox"
              ref={(input) => {
                if (input) input.indeterminate = isIndeterminate
              }}
              onChange={({ target: { checked } }) =>
                updateSelectedCategory(checked)
              }
              onClick={(e) => e.stopPropagation()}
              checked={allChecked}
            />
            <label
              htmlFor={`${selectedType}_${category}`}
              onClick={(e) => e.stopPropagation()}
            >
              {category}
              {someChecked && !allChecked && (
                <span
                  style={{
                    marginLeft: '8px',
                    color: '#a8a9ac',
                    fontSize: '14px',
                  }}
                >
                  (
                  {
                    items.filter((item) =>
                      checkedArray.some((c) => c.value === item.value),
                    ).length
                  }
                  /{items.length})
                </span>
              )}
            </label>
          </CheckboxDiv>
        </HeaderLabel>
      </ChecklabelHeader>
      <Accordion opened={opened}>
        <SelectableItems>
          {items?.map((option) => (
            <SelectableItemDiv key={`${selectedType}_${option.value}`}>
              <input
                id={`${selectedType}_${option.value}`}
                type="checkbox"
                onChange={({ target: { checked } }) =>
                  updateSelectedValue(option, checked)
                }
                checked={checkedArray.some(
                  ({ value }) => option.value === value,
                )}
              />
              <label htmlFor={`${selectedType}_${option.value}`}>
                {option.label}
              </label>
            </SelectableItemDiv>
          ))}
        </SelectableItems>
      </Accordion>
    </>
  )
}

type SolutionsCategoryInputFormProps = {
  category: string
  items: MasterDataWithCategory[]
  checkedArray: MasterData[]
  selectedType: Types
  setCheckedArray: Dispatch<SetStateAction<MasterData[]>>
}

const SolutionsCategoryInputForm: VFC<SolutionsCategoryInputFormProps> = ({
  category,
  items,
  checkedArray,
  selectedType,
  setCheckedArray,
}) => {
  const [opened, setOpened] = useState(false)

  const updateSelectedCategory = (checked: boolean) => {
    if (checked) {
      // カテゴリ内のアイテムを追加（重複を除外）
      const addCheckedArray = [
        ...checkedArray,
        ...items.filter(
          (item) => !checkedArray.some((c) => c.value === item.value),
        ),
      ]
      setCheckedArray(addCheckedArray)
    } else {
      // カテゴリ内のアイテムを除外
      const excludedCheckedArray = checkedArray.filter((item) => {
        const categoryItem = items.find((i) => i.value === item.value)
        if (!categoryItem) return true
        return item.value !== categoryItem.value
      })
      setCheckedArray(excludedCheckedArray)
    }
  }

  // カテゴリ内のすべてのアイテムがチェック済みかどうか
  const allChecked = items?.every(({ value }) => {
    const checked = checkedArray.find((checked) => checked.value === value)
    if (!checked) return false
    return checked.value === value
  })

  // カテゴリ内の一部のアイテムがチェック済みかどうか
  const someChecked = items?.some(({ value }) => {
    return checkedArray.some((checked) => checked.value === value)
  })

  const isIndeterminate = someChecked && !allChecked

  const updateSelectedValue = (selected: MasterData, checked: boolean) => {
    checked
      ? setCheckedArray([...checkedArray, selected])
      : setCheckedArray(
          checkedArray.filter((item) => item.value !== selected.value),
        )
  }

  return (
    <>
      <ChecklabelHeader onClick={() => setOpened(!opened)} opened={opened}>
        <HeaderLabel opened={opened}>
          <CheckboxDiv color={orange}>
            <input
              id={`${selectedType}_${category}`}
              type="checkbox"
              ref={(input) => {
                if (input) input.indeterminate = isIndeterminate
              }}
              onChange={({ target: { checked } }) =>
                updateSelectedCategory(checked)
              }
              onClick={(e) => e.stopPropagation()}
              checked={allChecked}
            />
            <label
              htmlFor={`${selectedType}_${category}`}
              onClick={(e) => e.stopPropagation()}
            >
              {category}
              {someChecked && !allChecked && (
                <span
                  style={{
                    marginLeft: '8px',
                    color: '#a8a9ac',
                    fontSize: '14px',
                  }}
                >
                  (
                  {
                    items.filter((item) =>
                      checkedArray.some((c) => c.value === item.value),
                    ).length
                  }
                  /{items.length})
                </span>
              )}
            </label>
          </CheckboxDiv>
        </HeaderLabel>
      </ChecklabelHeader>
      <Accordion opened={opened}>
        <SelectableItems>
          {items?.map((option) => (
            <SelectableItemDiv key={`${selectedType}_${option.value}`}>
              <input
                id={`${selectedType}_${option.value}`}
                type="checkbox"
                onChange={({ target: { checked } }) =>
                  updateSelectedValue(option, checked)
                }
                checked={checkedArray.some(
                  ({ value }) => option.value === value,
                )}
              />
              <label htmlFor={`${selectedType}_${option.value}`}>
                {option.label}
              </label>
            </SelectableItemDiv>
          ))}
        </SelectableItems>
      </Accordion>
    </>
  )
}
